import React, { useCallback } from 'react';
import { StyleSheet } from 'react-native';
import { View } from 'react-native';

import {
  LabelText,
  theme,
  useInaccessibleElement,
  useSafeIsFocused,
  useSafeNavigation,
  useSafeRouter,
} from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { Navbar as NavbarComponents } from '@order/navigation';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';
import { useTelemetry } from '@order/Telemetry';

import { useNavigateToLastInteractedStore } from '../../context/LastInteractedStore';
import { useBagCount, useLoyaltyAvailablePoints } from '../../hooks';

export const Navbar = () => {
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const isDemoBadgeEnabled = useFeatureFlag('CELS-4877-order-expo-router-demo-badge-enabled'); // prettier-ignore

  const { track } = useTelemetry();
  const navigation = useSafeNavigation();
  const router = useSafeRouter();
  const navigateLastStore = useNavigateToLastInteractedStore();

  // ─── Accessibility ─────────────────────────────────────────────────────────
  // Here we add the "accessibilityHidden" prop when the navbar isn't focused.
  // This is because with react-navigation we'll render this navbar multiple times.

  const isVisible = useSafeIsFocused();
  const a11yProps = useInaccessibleElement(!isVisible);

  // ─── Remote Data ───────────────────────────────────────────────────────────

  const points = useLoyaltyAvailablePoints();
  const { bagCount } = useBagCount();

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const navigateHome = useCallback(() => {
    track('home.jump_nav');

    if (isExpoRouterEnabled) {
      router?.navigate('/');
      return;
    }

    navigation?.navigate('MainTabs', {
      screen: 'HomeTab',
      params: { screen: 'Home' },
    });
  }, [track, isExpoRouterEnabled, router, navigation]);

  const navigateLoyalty = useCallback(() => {
    track('loyalty.jump_nav');

    if (isExpoRouterEnabled) {
      router?.navigate('/sg-rewards');
      return;
    }

    navigation?.navigate('MainTabs' , { screen: 'LoyaltyTab', params: { screen: 'LoyaltyHome' }}); // prettier-ignore
  }, [track, isExpoRouterEnabled, router, navigation]);

  const navigateGift = useCallback(() => {
    track('gift.jump_nav');

    if (isExpoRouterEnabled) {
      router?.navigate('/gift');
      return;
    }

    navigation?.navigate('MainTabs' , { screen: 'GiftCardsTab', params: { screen: 'GiftCards' }}); // prettier-ignore
  }, [track, isExpoRouterEnabled, router, navigation]);

  const navigateMenu = useCallback(() => {
    track('menu.jump_nav', {});
    navigateLastStore();
  }, [track, navigateLastStore]);

  const navigateOrder = useCallback(() => {
    track('order.jump_nav');
    navigateLastStore();
  }, [track, navigateLastStore]);

  const navigateBag = useCallback(() => {
    track('bag.jump_nav');

    if (isExpoRouterEnabled) {
      router?.navigate('/bag');
      return;
    }

    navigation?.navigate('Modal', { screen: 'Bag' });
  }, [track, isExpoRouterEnabled, router, navigation]);

  const navigateAccount = useCallback(() => {
    track('account.jump_nav');

    if (isExpoRouterEnabled) {
      router?.navigate('/account/menu');
      return;
    }

    navigation?.navigate('MainTabs' , { screen: 'AccountTab', params: undefined  }); // prettier-ignore
  }, [track, isExpoRouterEnabled, router, navigation]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <NavbarComponents.Containers.Main {...a11yProps}>
      <NavbarComponents.Containers.Left>
        <NavbarComponents.Gift onPress={navigateGift} />
        <NavbarComponents.Loyalty points={points} onPress={navigateLoyalty} />
        <NavbarComponents.Menu onPress={navigateMenu} />
      </NavbarComponents.Containers.Left>

      <NavbarComponents.Home onPress={navigateHome} />

      <NavbarComponents.Containers.Right>
        <NavbarComponents.Order onPress={navigateOrder} />
        <NavbarComponents.Bag count={bagCount} onPress={navigateBag} />
        <NavbarComponents.Account onPress={navigateAccount} />
      </NavbarComponents.Containers.Right>

      {isExpoRouterEnabled && isDemoBadgeEnabled ? (
        <View pointerEvents="none" style={styles.expoRouterBadge}>
          <LabelText sizeMatch={['12']}>Expo Router</LabelText>
        </View>
      ) : null}
    </NavbarComponents.Containers.Main>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  expoRouterBadge: {
    position: 'absolute',
    top: '100%',
    right: theme.spacing['4'],
    backgroundColor: theme.colors.LIME,
    borderBottomRightRadius: theme.spacing['2'],
    borderBottomLeftRadius: theme.spacing['2'],
    paddingVertical: theme.spacing['1'],
    paddingHorizontal: theme.spacing['2'],
    ...theme.elevations['1'],
  },
});
